import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const PlatformContext = createContext();

export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
};

export const PlatformProvider = ({ children }) => {
  const [platformStatus, setPlatformStatus] = useState({
    mode: 'unknown',
    is_live: false,
    is_demo: false,
    is_maintenance: false,
    maintenance_message: '',
    features_available: {
      trading: false,
      options: false,
      research: true,
      vibe: true,
      social_trading: true,
      admin_dashboard: true,
      user_registration: true
    }
  });
  const [loading, setLoading] = useState(true);
  const requestNumber = useRef(0);

  const fetchPlatformStatus = async () => {
    const current = ++requestNumber.current;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API}/api/platform-status`);
      if (current === requestNumber.current) setPlatformStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch platform status:', error);
      // Unknown is not confirmation of Demo. Signing fails closed separately.
      if (current === requestNumber.current) setPlatformStatus({
        mode: 'unknown',
        is_live: false,
        is_demo: false,
        is_maintenance: false,
        maintenance_message: '',
        features_available: {
          trading: false,
          options: false,
          research: true,
          vibe: true,
          social_trading: true,
          admin_dashboard: true,
          user_registration: true
        }
      });
    } finally {
      if (current === requestNumber.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatformStatus();
    
    // Check platform status every 5 minutes
    const interval = setInterval(fetchPlatformStatus, 5 * 60 * 1000);
    window.addEventListener('focus', fetchPlatformStatus);
    
    return () => { clearInterval(interval); window.removeEventListener('focus', fetchPlatformStatus); };
  }, []);

  const isFeatureEnabled = (feature) => {
    return platformStatus.features_available[feature] || false;
  };

  const isDemoMode = () => {
    return platformStatus.is_demo;
  };

  const isLiveMode = () => {
    return platformStatus.is_live;
  };

  const isMaintenanceMode = () => {
    return platformStatus.is_maintenance;
  };

  const value = {
    platformStatus,
    loading,
    isFeatureEnabled,
    isDemoMode,
    isLiveMode,
    isMaintenanceMode,
    refreshStatus: fetchPlatformStatus
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
};

export default PlatformContext;